import * as React from 'react';

import { DialogDescription } from '@radix-ui/react-dialog';
import { useIntl } from 'react-intl';

import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useRefundReservationPayment } from 'src/features/reservations/hooks/useRefundReservationPayment';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface RefundPaymentModalProps extends React.HTMLAttributes<HTMLDivElement> {
    reservationNumber: string;
    reservationId: string;
    refetch: () => void;
}

const RefundPaymentModal = ({ reservationNumber, reservationId, refetch, children }: RefundPaymentModalProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: refundReservationPayment } = useRefundReservationPayment({
        onSuccess: () => {
            setIsModalOpen(!isModalOpen);
            void refetch();
            toast({
                title: formatMessage(reservationMessages.refundPaymentSuccess),
                description: formatMessage(reservationMessages.refundPaymentSuccessDescription, {
                    reservationNumber,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={() => setIsModalOpen(!isModalOpen)}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.PAY_MASHINE_VLD} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.refundPayment)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.refundPaymentDescription, { reservationNumber })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(reservationMessages.refundPaymentConfirmation, { reservationNumber })}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={() => {
                            if (reservationNumber) {
                                void refundReservationPayment({ parkingId, reservationId: reservationId });
                            }
                        }}
                        className="flex-1"
                    >
                        {formatMessage(reservationMessages.refundPayment)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RefundPaymentModal;
